import { Injectable } from '@angular/core';

export interface VideoStallHandlerConfig {
  /** Durée d'attente (ms) avant de déclarer la vidéo bloquée. Par défaut 8 000 ms. */
  stallTimeoutMs?: number;
}

interface StallHandlerState {
  waitingHandler: () => void;
  stalledHandler: () => void;
  playingHandler: () => void;
  timeoutId: number | null;
}

/**
 * Service partagé qui détecte les blocages réseau en cours de lecture vidéo
 * (événements `waiting` / `stalled`) et déclenche un callback de fallback après
 * un délai configurable. Utiliser `detachStallHandlers` lors du nettoyage pour
 * éviter les fuites mémoire.
 */
@Injectable({ providedIn: 'root' })
export class VideoStallHandlerService {

  private readonly DEFAULT_STALL_TIMEOUT_MS = 8000;

  /**
   * WeakMap pour stocker l'état par élément vidéo sans empêcher le GC.
   * Plusieurs composants peuvent utiliser le service en parallèle.
   */
  private readonly states = new WeakMap<HTMLVideoElement, StallHandlerState>();

  /**
   * Attache les gestionnaires de blocage sur l'élément vidéo fourni.
   * Toute attache précédente sur le même élément est d'abord retirée.
   *
   * @param video          L'élément <video> à surveiller
   * @param onFallback     Callback appelé quand le blocage dépasse le timeout
   *                       (typiquement `moveToNextNews()`)
   * @param config         Options de configuration (timeout, etc.)
   */
  attachStallHandlers(
    video: HTMLVideoElement,
    onFallback: () => void,
    config?: VideoStallHandlerConfig
  ): void {
    // Nettoyage préventif en cas de ré-attache
    this.detachStallHandlers(video);

    const stallTimeoutMs = config?.stallTimeoutMs ?? this.DEFAULT_STALL_TIMEOUT_MS;

    // Référence mutable partagée par les closures
    const state: StallHandlerState = {
      waitingHandler: () => {},
      stalledHandler: () => {},
      playingHandler: () => {},
      timeoutId: null
    };

    const clearStallTimeout = () => {
      if (state.timeoutId !== null) {
        window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
      }
    };

    const startStallTimeout = () => {
      clearStallTimeout();
      state.timeoutId = window.setTimeout(() => {
        state.timeoutId = null;
        console.warn(`VideoStallHandlerService : blocage réseau détecté (>${stallTimeoutMs} ms) – passage à la news suivante`);
        onFallback();
      }, stallTimeoutMs);
    };

    state.waitingHandler = () => {
      console.warn('VideoStallHandlerService : événement "waiting" reçu, démarrage du timeout de blocage');
      startStallTimeout();
    };

    state.stalledHandler = () => {
      console.warn('VideoStallHandlerService : événement "stalled" reçu, démarrage du timeout de blocage');
      startStallTimeout();
    };

    state.playingHandler = () => {
      clearStallTimeout();
    };

    video.addEventListener('waiting', state.waitingHandler);
    video.addEventListener('stalled', state.stalledHandler);
    video.addEventListener('playing', state.playingHandler);

    this.states.set(video, state);
  }

  /**
   * Retire tous les gestionnaires et annule le timeout en cours pour l'élément.
   * Méthode idempotente : sans effet si l'élément n'est pas connu.
   */
  detachStallHandlers(video: HTMLVideoElement | undefined): void {
    if (!video) return;

    const state = this.states.get(video);
    if (!state) return;

    if (state.timeoutId !== null) {
      window.clearTimeout(state.timeoutId);
    }

    video.removeEventListener('waiting', state.waitingHandler);
    video.removeEventListener('stalled', state.stalledHandler);
    video.removeEventListener('playing', state.playingHandler);

    this.states.delete(video);
  }

  /**
   * Retire les listeners 'ended', 'loadeddata', 'canplay' et les handlers de blocage
   * sur l'élément vidéo fourni, puis neutralise onerror.
   * Méthode idempotente : sans effet si video est undefined.
   */
  cleanupVideoElement(
    video: HTMLVideoElement | undefined,
    endedHandler?: () => void,
    loadedDataHandler?: () => void
  ): void {
    if (!video) return;
    this.detachStallHandlers(video);
    if (endedHandler) {
      video.removeEventListener('ended', endedHandler);
    }
    if (loadedDataHandler) {
      video.removeEventListener('loadeddata', loadedDataHandler);
      video.removeEventListener('canplay', loadedDataHandler);
    }
    video.onerror = null;
  }
}
